import { api } from '../network/api';

/**
 * Preview-build runtime utilities.
 *
 * All checks are based on the server's /api/site-info response (runtime),
 * NOT on build-time env vars. This means the same frontend bundle works
 * for both normal and preview deployments — only the backend env differs.
 */

export interface SiteInfo {
  preview: boolean;
  company: {
    name: string;
    email: string;
    address: string;
    registration: string;
    jurisdiction: string;
    country: string;
    domain: string;
    gameName: string;
  };
  features: {
    kyc: boolean;
    withdrawals: boolean;
    invoices: boolean;
  };
  socialLinks: Array<{ platform: string; url: string }>;
}

let cachedSiteInfo: SiteInfo | null = null;

/**
 * Fetch site-info from the backend. Cached after first call.
 * Called once during app boot (before render).
 */
export async function fetchSiteInfo(): Promise<SiteInfo> {
  if (cachedSiteInfo) return cachedSiteInfo;

  try {
    cachedSiteInfo = await api.get<SiteInfo>('/site-info');
  } catch {
    // Fallback: treat as non-preview if endpoint is unreachable
    cachedSiteInfo = {
      preview: false,
      company: {
        name: '',
        email: '',
        address: '',
        registration: '',
        jurisdiction: '',
        country: '',
        domain: '',
        gameName: 'Game',
      },
      features: { kyc: false, withdrawals: false, invoices: false },
      socialLinks: [],
    };
  }
  return cachedSiteInfo;
}

/** Returns true when the backend is running in preview mode. */
export function isPreviewBuild(): boolean {
  return cachedSiteInfo?.preview ?? false;
}

/** Returns the current site info (must call fetchSiteInfo first). */
export function getSiteInfo(): SiteInfo | null {
  return cachedSiteInfo;
}

/** Returns the game name — "Game Preview" in preview mode, real name otherwise. */
export function previewGameName(): string {
  if (isPreviewBuild()) return 'Game Preview';
  return cachedSiteInfo?.company.gameName || 'Game';
}

/**
 * Strips brand-identifying meta tags from the DOM.
 * Called after site-info loads when preview mode is detected.
 *
 * Removes: OG tags, twitter cards, description, favicon, apple-touch-icon.
 * Sets document title to "Game Preview".
 */
export function stripBrandMeta(): void {
  document.title = 'Game Preview';

  const selectors = [
    'meta[name="description"]',
    'meta[property^="og:"]',
    'meta[name^="twitter:"]',
    'meta[name="robots"]',
    'link[rel="icon"]',
    'link[rel="shortcut icon"]',
    'link[rel="apple-touch-icon"]',
  ];

  for (const sel of selectors) {
    document.querySelectorAll(sel).forEach((el) => el.remove());
  }
}
